import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TableModule } from 'primeng/table';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ApiModule } from './api/api.module';
import { APP_BASE_HREF, DatePipe } from '@angular/common';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ToastComponent } from './shared/toast/toast.component';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';

import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { noCacheInterceptor } from './interceptors/no-cache.interceptor';

registerLocaleData(localeDe, 'de-AT', localeDeExtra);

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json?v=' + Date.now());
}

export function initializeKeycloak(keycloak: KeycloakService) {
  return () =>
    keycloak
      .init({
        config: {
          url: (window as any).spaConfig.endpoints.keycloakUrl,
          realm: (window as any).spaConfig.endpoints.realm,
          clientId: (window as any).spaConfig.endpoints.clientId,
        },
        initOptions: {
          onLoad: 'login-required',
          flow: 'standard',
          checkLoginIframe: false,
        },
      })
      .catch((error) => {
        /* error handling */
        console.log(error);
      });
}
export function initializeTranslations(translate: TranslateService) {
  return async () => {
    // Supported languages
    translate.addLangs(['de']);
    // Use german as default
    translate.setDefaultLang('de');

    // Set translation to browser language if we support it
    const browserLang = translate.getBrowserLang() ?? 'de';
    if (translate.langs.indexOf(browserLang) < 0) {
      translate.use('de');
    } else {
      translate.use(browserLang);
    }

    const translatePromise = new Promise<void>((resolve) => {
      translate.onLangChange.subscribe(() => {
        resolve();
      });
    });

    return translatePromise;
  };
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    KeycloakAngularModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: 'de',
    }),
    TableModule,
    BrowserAnimationsModule,
    ApiModule.forRoot({
      rootUrl: (window as any).spaConfig.endpoints.api,
    }),
    ToastModule,
    ToastComponent,
    CalendarModule.forRoot({ provide: DateAdapter, useFactory: adapterFactory }),
  ],
  providers: [
    DatePipe,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      deps: [KeycloakService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslations,
      deps: [TranslateService],
      multi: true,
    },
    { provide: APP_BASE_HREF, useValue: '/bestatterportal' },
    MessageService,
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([noCacheInterceptor])),
  ],
})
export class AppModule {}
